import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Display a voucher code with cosmetic dashes: ABCD1234EFGH5678 → ABCD-1234-EFGH-5678
// Handles bare 16-char codes and already-dashed codes gracefully.
export function formatVoucherCode(code: string): string {
  const bare = code.replace(/-/g, "").toUpperCase();
  if (bare.length !== 16) return code;
  return `${bare.slice(0, 4)}-${bare.slice(4, 8)}-${bare.slice(8, 12)}-${bare.slice(12, 16)}`;
}

// Returns ****-****-****-*XXX where XXX = last 3 chars of the raw code.
export const maskVoucherCode = (code: string): string => {
  const raw = code.replace(/-/g, "");
  if (raw.length < 3) return "***";
  return `****-****-****-*${raw.slice(-3)}`;
};

// Format currency as GHS with locale-specific formatting
export function formatGHS(value: number, minimumFractionDigits: number = 2): string {
  return `GHS ${value.toLocaleString("en-GH", { minimumFractionDigits })}`;
}

// Multi-country currency formatter.
// Pass the currency_symbol from platform_countries (e.g. 'GH₵', '₦').
// Falls back to GHS display when symbol is omitted.
export function formatCurrency(
  value: number,
  currencySymbol: string = "GH₵",
  minimumFractionDigits: number = 2
): string {
  const formatted = value.toLocaleString("en", { minimumFractionDigits, maximumFractionDigits: 2 });
  return `${currencySymbol} ${formatted}`;
}

// Country-code → locale map for number formatting
const COUNTRY_LOCALE: Record<string, string> = {
  GH: "en-GH",
  NG: "en-NG",
};

// Format a monetary value given a country code and currency symbol.
// Usage: formatCountryCurrency(1500, 'NG', '₦') → '₦ 1,500.00'
export function formatCountryCurrency(
  value: number,
  countryCode: string = "GH",
  currencySymbol: string = "GH₵",
  minimumFractionDigits: number = 2
): string {
  const locale = COUNTRY_LOCALE[countryCode] ?? "en";
  const formatted = value.toLocaleString(locale, { minimumFractionDigits, maximumFractionDigits: 2 });
  return `${currencySymbol} ${formatted}`;
}
